import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router'
import { Button, Stack, Text } from '@/design-system'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { STEP_NUMBER, TOTAL_STEPS, useOnboardingDraft, useStep } from './progress'
import styles from './Onboarding.module.css'

const LENGTH = 6
const RESEND_SECONDS = 30

/** 07 SMS · 08 Email · WhatsApp — Step 2 of 5, the code itself. */
export function OnboardingCode() {
  useStep('code')
  const navigate = useNavigate()
  const [draft, update] = useOnboardingDraft()
  const [digits, setDigits] = useState<string[]>(() => Array(LENGTH).fill(''))
  const [seconds, setSeconds] = useState(RESEND_SECONDS)
  const boxes = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    boxes.current[0]?.focus()
  }, [draft.channel])

  useEffect(() => {
    if (seconds <= 0) return
    const timer = window.setTimeout(() => setSeconds((s) => s - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [seconds])

  const code = digits.join('')
  const complete = code.length === LENGTH

  const set = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    setDigits((current) => current.map((d, i) => (i === index ? digit : d)))
    if (digit && index < LENGTH - 1) boxes.current[index + 1]?.focus()
  }

  const onKey = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) boxes.current[index - 1]?.focus()
  }

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH)
    if (!pasted) return
    event.preventDefault()
    setDigits(Array.from({ length: LENGTH }, (_, i) => pasted[i] ?? ''))
    boxes.current[Math.min(pasted.length, LENGTH - 1)]?.focus()
  }

  const byEmail = draft.channel === 'email'
  const copy = byEmail
    ? {
        title: 'Check your email',
        body: `We sent a ${LENGTH}-digit code to ${draft.email || 'your email'}. It expires in 10 minutes.`,
        switchLabel: 'Send it to my phone instead',
        switchTo: 'sms' as const,
      }
    : draft.channel === 'sms'
      ? {
          title: 'Enter the code',
          body: `Texted to ${draft.phone || 'your phone'}.`,
          switchLabel: 'Send it by email instead',
          switchTo: 'email' as const,
        }
      : {
          title: 'Enter the code',
          body: `Sent to your WhatsApp at ${draft.phone || 'your number'}.`,
          switchLabel: 'Text it to me instead',
          switchTo: 'sms' as const,
        }

  const switchChannel = () => {
    update({ channel: copy.switchTo })
    setDigits(Array(LENGTH).fill(''))
    setSeconds(RESEND_SECONDS)
  }

  return (
    <FlowLayout
      step={STEP_NUMBER.code}
      totalSteps={TOTAL_STEPS}
      back="/onboarding/verify"
      footer={
        <>
          <Button size="lg" block disabled={!complete} onClick={() => navigate('/onboarding/identity')}>
            Verify
          </Button>
          <Button variant="ghost" size="md" block onClick={switchChannel}>
            {copy.switchLabel}
          </Button>
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text as="h1" variant="title" weight="bold">
            {copy.title}
          </Text>
          <Text variant="body" tone="secondary">
            {copy.body}
          </Text>
        </Stack>

        <div className={styles.otp} role="group" aria-label={`${LENGTH}-digit code`}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                boxes.current[index] = el
              }}
              className={styles.otpBox}
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              aria-label={`Digit ${index + 1}`}
              onChange={(event) => set(index, event.target.value)}
              onKeyDown={(event) => onKey(index, event)}
              onPaste={onPaste}
            />
          ))}
        </div>

        <Stack direction="row" align="center" gap={2}>
          <Text variant="caption" tone="secondary">
            Didn’t get it?
          </Text>
          {seconds > 0 ? (
            <Text variant="caption" weight="semibold" numeric>
              Resend in 0:{String(seconds).padStart(2, '0')}
            </Text>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setSeconds(RESEND_SECONDS)}>
              Resend code
            </Button>
          )}
        </Stack>

        <Text variant="caption" tone="secondary">
          We will never ask you for this code by phone or chat.
        </Text>
      </Stack>
    </FlowLayout>
  )
}
